const getCompoundEntityTitle = require("./getCompoundEntityTitle");
const fetchEntities = require("./fetchEntities");
const fetchEntity = require("./fetchEntity");
const fetchImage = require("./fetchImage");
const fetchVideo = require("./fetchVideo");
const fetchNavItem = require("./fetchNavItem");
const getFetchHeaders = require("./getFetchHeaders");
const getMediaDownloadPath = require("./getMediaDownloadPath");
const findCategory = require("./findCategory");
const findFeaturedImage = require("./findFeaturedImage");
const findFeaturedVideo = require("./findFeaturedVideo");
const findEntityDates = require("./findEntityDates");
const fetchCategories = require("./fetchCategories");
const generateJsonApiIncludes = require("./generateJsonApiIncludes");
const generateJsonApiFilters = require("./generateJsonApiFilters");

/**
 * Fetch a list of entities to render inside a list block
 *
 * @param entities_list: a node--entities_list entity
 * @param configs: a list of all configurations coming from plugin options (nodesConfig.json)
 * @param includes: a list of full entities when available to prevent additional fetching (optional)
 * @param sitemap: an object with site navigation data (optional)
 * @param isAuthenticated: an understory auth boolean
 * @param relationships: relationships of the parent entity containing the list, used to display relationships (required if list_data.field_content_type === true)
 */

module.exports = async function fetchList({
	list_data,
	configs,
	includes = null,
	isAuthenticated = null,
	relationships = null,
	paged = null,
	limit,
}) {
	let basic_headers = !isAuthenticated ? getFetchHeaders() : null;
	limit = limit || list_data.limit;
	async function fetchSingleEntity(_entity) {
		let type = _entity.type.split("--");
		let single_endpoint = `${type[0]}/${type[1]}/${_entity.id}`;
		try {
			return await fetchEntity({
				endpoint: single_endpoint,
				headers: basic_headers,
				maxDepth: 0,
			});
		} catch (error) {
			return null;
		}
	}
	/**
	 * Extract just the neccesary data to render an entity on a list
	 */
	async function lightenEntity(_entity, index) {
		let mapPropsFromList = [
			"flush",
			"thumbOption",
			"headingComponent",
			"datePosition",
			"thumbnailPosition",
			"thumbnailSize",
		];
		let config = configs[_entity.type];
		let kind = _entity.type.includes("media--") ? "media" : "node";
		let light_e = {
			id: _entity.id,
			type: _entity.type,
			title: kind === "node" ? _entity.attributes.title : _entity.attributes.name,
			changed: _entity.attributes.changed,
			created: _entity.attributes.created,
			date_iso: _entity.attributes.field_start_date
				? _entity.attributes.field_start_date
				: _entity.attributes.created,

			kind,
		};

		if (
			config &&
			config.options &&
			config.options.list &&
			config.options.list.attributes
		) {
			Object.keys(config.options.list.attributes).forEach((attr) => {
				if (_entity.attributes[attr]) {
					if (!_entity.attributes[attr].processed) {
						light_e[attr] = _entity.attributes[attr];
					} else {
						light_e[attr] = _entity.attributes[attr].processed;
					}
				}
			});
			light_e["extra_attributes"] = config.options.list.attributes;
		}

		if (kind === "media") {
			let download_path = getMediaDownloadPath(_entity);
			light_e.slug = download_path.uri;
			light_e.external = light_e.slug && light_e.slug.includes("http");
			light_e.download = !light_e.external;
		} else {
			let nav_item;
			if (_entity.attributes.field_to && config && config.external) {
				nav_item = { slug: _entity.attributes.field_to.uri };
			} else if (config && !config.skipPageCreation) {
				nav_item = await fetchNavItem(_entity);
			}

			light_e = {
				...light_e,
				promote: _entity.attributes.promote,
				sticky: _entity.attributes.sticky,
				slug: nav_item ? nav_item.slug : null,
				download: false,
			};

			light_e.external = light_e.slug && light_e.slug.includes("http");

			if (
				config.options &&
				config.options.excerpt &&
				config.options.excerpt.includeBody &&
				_entity.attributes.body &&
				_entity.attributes.body.processed
			) {
				light_e.body = _entity.attributes.body.processed;
			}

			if (_entity.attributes.field_summary) {
				light_e.summary = _entity.attributes.field_summary;
			}
			if (_entity.attributes.field_subtitle) {
				light_e.subtitle = _entity.attributes.field_subtitle;
			}

			let dates = findEntityDates(_entity.attributes, config);

			if (dates) {
				light_e.dates = dates;
				light_e.dateFormat = config && config.dateFormat ? config.dateFormat : "D";
			}

			/**
			 * build complex entity titles if needed
			 */
			if (config && config.titleSchema) {
				light_e.title = getCompoundEntityTitle(config.titleSchema, _entity.attributes);
			}

			if (_entity.relationships) {
				/**
				 * fetch the cover image for the entity
				 */

				if (
					((list_data.firstThumbOnly && index === 0) || !list_data.firstThumbOnly) &&
					list_data.thumbOption !== "hide"
				) {
					let _image_rel = findFeaturedImage(_entity.relationships);
					let _image = _image_rel ? await fetchImage(_image_rel) : null;
					if (_image) {
						light_e.image = _image;
					}
					let _video_rel = findFeaturedVideo(_entity.relationships);
					let _video = _video_rel ? await fetchVideo(_video_rel) : null;
					if (_video) {
						if (_image) {
							_video.thumbnail = _image;
						}
						light_e.video = _video;
					}
				}

				// fetch category information
				let _cats = await findCategory(_entity.relationships);
				if (_cats && _cats.length > 0) {
					let fetched_categories = await fetchCategories(_cats);
					if (fetched_categories !== null) {
						fetched_categories.forEach((_cat) => {
							if (!light_e.tags) {
								light_e.tags = [];
							}

							let label = _cat.type.replace(/taxonomy_term--/i, "");
							label = label
								.split("_")
								.map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize first letter of each word
								.join(" ");

							let existingTag = light_e.tags.find((tag) => tag.label === label);
							if (existingTag) {
								existingTag.items.push({ title: _cat.name });
							} else {
								light_e.tags.push({ label: label, items: [{ title: _cat.name }] });
							}
						});
					}
				}
			}

			if (_entity.type === "user--user") {
				light_e.mail = _entity.attributes.mail;
				light_e.phone = _entity.attributes.field_phone;
				light_e.jobTitle = _entity.attributes.field_job_title;
			} else {
				if (config && config.options && config.options.vCard) {
					if (_entity.attributes.field_subtitle) {
						light_e.jobTitle = _entity.attributes.field_subtitle;
					}
				}
			}
		}

		for (const prop_name of mapPropsFromList) {
			if (typeof list_data[prop_name] !== "undefined") {
				light_e[prop_name] = list_data[prop_name];
			}
		}

		// Map column values
		if (list_data["mode"] == "table" && list_data["field_table_columns"]) {
			let keyLabels = list_data["field_table_columns"].split("\r\n");

			keyLabels?.forEach((keyLabelItem) => {
				const keyLabelSplitted = keyLabelItem.split("|");

				if (Array.isArray(keyLabelSplitted)) {
					let field_name = keyLabelSplitted[0];
					field_name = field_name?.trim();
					if (field_name && _entity.attributes[field_name]) {
						light_e[field_name] = _entity.attributes[field_name];
					}
				}
			});
		}

		if (typeof config?.titleAppend == "string") {
			if (_entity.attributes[config?.titleAppend]) {
				light_e.title_append = _entity.attributes[config?.titleAppend];
			}
		}
		return light_e;
	}

	async function _fetchExcerpt(_entity, index) {
		if (typeof _entity.attributes !== "undefined") {
			return await lightenEntity(_entity, index);
		} else {
			let single_entity = fetchSingleEntity(_entity);
			if (single_entity && single_entity.entity && single_entity.entity.id) {
				return await lightenEntity(single_entity.entity, index);
			} else {
				return null;
			}
		}
	}

	/**
	 * fetch entity excerpts according to list specs
	 */

	let excerpts = [];
	let page = null;

	if (list_data.field_entities_list_by_specific) {
		// if specific entities are listed, fetch them
		for (let _sci = 0; _sci < list_data.field_entities_list_by_specific.length; _sci++) {
			const specific_entity = list_data.field_entities_list_by_specific[_sci];
			let _entity =
				includes !== null && typeof includes[specific_entity.id] !== "undefined"
					? includes[specific_entity.id]
					: specific_entity;
			let _excerpt = await _fetchExcerpt(_entity, _sci);
			if (_excerpt) {
				excerpts.push(_excerpt);
			}
		}
	} else {
		// if filtering by term, add the term's type to the list of contraints for the jsonapi query
		let jsonapi_constraints = {};

		if (
			list_data.field_entities_list_by_terms ||
			list_data.field_entities_list_by_content
		) {
			let items = [];
			if (list_data.field_entities_list_by_terms) {
				items = items.concat(list_data.field_entities_list_by_terms);
			}
			if (list_data.field_entities_list_by_content) {
				items = items.concat(list_data.field_entities_list_by_content);
			}

			jsonapi_constraints.by_rel = {};

			for (const an_item of items) {
				let bundle = an_item.type.split("--")[1];
				if (typeof jsonapi_constraints.by_rel[bundle] === "undefined") {
					jsonapi_constraints.by_rel[bundle] = [];
				}
				jsonapi_constraints.by_rel[bundle].push(an_item.id);
			}
		}

		if (
			list_data.types &&
			list_data.types.length > 0 // if listing by node or media type, fetch all entities of those types
		) {
			let _entities = [];
			for (const a_type of list_data.types) {
				let is_media = a_type.type.includes("media");
				let a_type_name = a_type.meta.drupal_internal__target_id;
				if (a_type_name) {
					let a_kind = is_media ? "media" : "node";
					let a_type_endpoint_sticky = `${a_kind}/${a_type_name}`;
					let a_type_endpoint = `${a_kind}/${a_type_name}`;
					let config = configs[`${a_kind}--${a_type_name}`];
					if (config) {
						let filters_sticky = generateJsonApiFilters({
							config,
							sticky: true,
							constraints: jsonapi_constraints,
							list_data,
						});
						let filters = generateJsonApiFilters({
							config,
							sticky: false,
							constraints: jsonapi_constraints,
							list_data,
						});
						if (filters.length) {
							a_type_endpoint_sticky += "?" + filters_sticky.join("&");
							a_type_endpoint += "?" + filters.join("&");
						}

						// Get includes
						let apiIncludes = generateJsonApiIncludes(config);
						if (apiIncludes) {
							a_type_endpoint_sticky += (filters.length > 0 ? "&" : "?") + apiIncludes;
							a_type_endpoint += (filters.length > 0 ? "&" : "?") + apiIncludes;
						}
					}

					let sticky_entities_of_type = await fetchEntities({
						endpoint: a_type_endpoint_sticky,
						headers: basic_headers,
						limit: 0,
					});
					let entities_of_type = await fetchEntities({
						endpoint: a_type_endpoint,
						headers: basic_headers,
						limit: limit,
						pagination: list_data.pagination,
					});
					_entities = [
						..._entities,
						...sticky_entities_of_type.data,
						...entities_of_type.data,
					];
					page = entities_of_type.page;
				}
			}
			for (let _ei = 0; _ei < _entities.length; _ei++) {
				const _e = _entities[_ei];
				let _le = await lightenEntity(_e, _ei);

				if (_e.attributes && _e.attributes.field_product_new_badge !== undefined) {
					_le.newBadge = _e.attributes.field_product_new_badge;
				}

				if (_e.attributes && _e.attributes.tags !== undefined) {
					_le.tags = _e.attributes.tags;
				}

				excerpts.push(_le);
			}
		} else if (
			relationships &&
			list_data.field_content_type // if Relationships Display: list related entities of given type
		) {
			const ctype = a_list.relationships.field_content_type;
			if (ctype) {
				const rel_field_name = `field_${ctype.split("node--")[1]}`;
				if (
					typeof relationships[rel_field_name] !== "undefined" &&
					relationships[rel_field_name].data
				) {
					const rel_ctype_data = Array.isArray(relationships[rel_field_name].data)
						? relationships[rel_field_name].data
						: [relationships[rel_field_name].data];

					for (let a_rel_i = 0; a_rel_i < rel_ctype_data.length; a_rel_i++) {
						const a_rel = rel_ctype_data[a_rel_i];
						let single_entity = fetchSingleEntity(a_rel);
						if (single_entity && single_entity.entity && single_entity.entity.id) {
							let _le = await lightenEntity(single_entity.entity, a_rel_i);
							excerpts.push(_le);
						}
					}
				}
			}
		} else if (list_data.field_entities_ulist_all) {
			let full_users = await fetchEntities({
				endpoint: "user/user",
				headers: basic_headers,
			});
			for (let _ui = 0; _ui < full_users.data.length; _ui++) {
				const _u = full_users.data[_ui];
				let _lu = await lightenEntity(_u, _ui);
				excerpts.push(_lu);
			}
		}
		if (limit && excerpts.length > limit) {
			excerpts = excerpts.slice(0, limit);
		}
	}
	return { items: excerpts, page };
};
